import { useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import {
  Box,
  Card,
  CardContent,
  EditIcon,
  EyeIcon,
  IconButton,
  SearchInput,
  Table,
  TrashIcon,
  Typography
} from "@dealer-center/core-components";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import type {
  NrtRecord,
  NrtRecordPermissions
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { StatusChip } from "./StatusChip";
import { compareNrtDates, formatNrtDate, renderOrPlaceholder } from "./nrtDate";

// Exported so specs assert against the real value instead of a copy that drifts.
export const PAGE_SIZE = 10;

export interface RecordsTableProps {
  permissions: NrtRecordPermissions;
  records: NrtRecord[];
  isLoading?: boolean;
  onDeleteRecord: (record: NrtRecord) => void;
  onEditRecord: (record: NrtRecord) => void;
  onLookRecord: (record: NrtRecord) => void;
}

interface BuildColumnsOptions {
  permissions: NrtRecordPermissions;
  onDeleteRecord: (record: NrtRecord) => void;
  onEditRecord: (record: NrtRecord) => void;
  onLookRecord: (record: NrtRecord) => void;
}

// Only fields shown as columns below (id/caseNumber/createdBy excluded).
const SEARCHABLE_FIELDS = [
  "accountId",
  "providerId",
  "status",
  "type",
  "classification",
  "select",
  "map",
  "dateCreated",
  "expirationDate"
] as const satisfies readonly (keyof NrtRecord)[];

// Matched against displayed text, not the stored value: cells show
// "Jun 11, 2026" while the record holds "1781151838".
const DATE_FIELDS = new Set<keyof NrtRecord>(["dateCreated", "expirationDate"]);

const matchesSearchTerm = (record: NrtRecord, term: string) =>
  SEARCHABLE_FIELDS.some(field => {
    const value = record[field];
    if (value === null || value === undefined) {
      return false;
    }
    const text = DATE_FIELDS.has(field) ? formatNrtDate(value) : String(value);
    return text.toLowerCase().includes(term);
  });

type CellComparator = (a: unknown, b: unknown) => number;

const compareStrings: CellComparator = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""));

// Date columns pass compareNrtDates; a text sort would order by month name.
const sortByField =
  (field: keyof NrtRecord, compare: CellComparator = compareStrings) =>
  (a: NrtRecord, b: NrtRecord) =>
    compare(a[field], b[field]);

// A factory, not a module-level array: the Actions column closes over
// onEditRecord/permissions, neither of which exists at module scope.
const buildColumns = ({
  permissions,
  onDeleteRecord,
  onEditRecord,
  onLookRecord
}: BuildColumnsOptions): TypeSafeTableColumns<NrtRecord> => [
  {
    dataIndex: "accountId",
    title: "Account ID",
    sorter: sortByField("accountId")
  },
  {
    dataIndex: "providerId",
    title: "Provider ID",
    sorter: sortByField("providerId")
  },
  {
    dataIndex: "status",
    title: "Status",
    render: (status: NrtRecord["status"]) => <StatusChip status={status} />,
    sorter: sortByField("status")
  },
  {
    dataIndex: "type",
    title: "Type",
    render: renderOrPlaceholder,
    sorter: sortByField("type")
  },
  {
    dataIndex: "classification",
    title: "Classification",
    render: renderOrPlaceholder,
    sorter: sortByField("classification")
  },
  {
    dataIndex: "select",
    title: "Select",
    render: renderOrPlaceholder,
    sorter: sortByField("select")
  },
  {
    dataIndex: "map",
    title: "Map",
    render: renderOrPlaceholder,
    sorter: sortByField("map")
  },
  {
    dataIndex: "expirationDate",
    title: "Expiration Date",
    render: formatNrtDate,
    sorter: sortByField("expirationDate", compareNrtDates)
  },
  {
    dataIndex: "dateCreated",
    title: "Date Created",
    render: formatNrtDate,
    sorter: sortByField("dateCreated", compareNrtDates)
  },
  {
    title: "Actions",
    columnSortable: false,
    render: (_value: unknown, record: NrtRecord) => {
      const canEdit = permissions.canEditRecord(record);
      const canDelete = permissions.canDeleteRecord(record);
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* Ungated: it only shows data this table already has. */}
          <IconButton
            aria-label={`Look at case ${record.caseNumber} for ${record.accountId}`}
            buttonStyle="tertiary"
            size="sm"
            onClick={() => onLookRecord(record)}
          >
            <EyeIcon title="Look" />
          </IconButton>
          <IconButton
            aria-label={`Edit case ${record.caseNumber} for ${record.accountId}`}
            buttonStyle="tertiary"
            disabled={!canEdit}
            size="sm"
            onClick={() => onEditRecord(record)}
          >
            <EditIcon title="Edit" />
          </IconButton>
          <IconButton
            aria-label={`Delete case ${record.caseNumber} for ${record.accountId}`}
            buttonStyle="tertiary"
            disabled={!canDelete}
            size="sm"
            onClick={() => onDeleteRecord(record)}
          >
            <TrashIcon title="Delete" />
          </IconButton>
        </Box>
      );
    }
  }
];

export const RecordsTable = ({
  permissions,
  records,
  isLoading = false,
  onDeleteRecord,
  onEditRecord,
  onLookRecord
}: RecordsTableProps) => {
  const [searchValue, setSearchValue] = useState("");

  const columns = useMemo(
    () =>
      buildColumns({
        permissions,
        onDeleteRecord,
        onEditRecord,
        onLookRecord
      }),
    [permissions, onDeleteRecord, onEditRecord, onLookRecord]
  );

  const filteredRecords = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) {
      return records;
    }
    return records.filter(record => matchesSearchTerm(record, term));
  }, [records, searchValue]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value ?? "");
  };

  return (
    // minWidth: 0 lets this flex item shrink instead of forcing the page
    // (a flex column) to overflow to fit the table's content width.
    <Card
      content={
        <CardContent>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 2
            }}
          >
            <Typography sx={{ fontWeight: "bold" }} variant="h4">
              Mapping Records {isLoading ? "" : `(${filteredRecords.length})`}
            </Typography>
            <SearchInput
              data-testid="nrt-mapping-tool-search-input"
              inputValue={searchValue}
              placeholder="Search records..."
              sx={{ maxWidth: "300px" }}
              onClearCallback={() => setSearchValue("")}
              onInputChange={handleSearchChange}
            />
          </Box>
          {/* No scrollX/tableLayout — columns shrink to fit, like franchise-management's table. */}
          <Table.Table
            sortableColumns
            columns={columns}
            currentPageSize={PAGE_SIZE}
            data={filteredRecords}
            dataDensity="small"
            emptyText="No mapping records found"
            isLoading={isLoading}
            rowKey="id"
          />
        </CardContent>
      }
      sx={{ minWidth: 0 }}
    />
  );
};
